import { createAppSelector } from "../store/utils.js";
import {
  FooterModuleSettings,
  Language,
  LogoCategories,
  LogoCategory,
  Module,
  StoreState,
  TranslatedPartner,
  TranslatedPartnerLogo,
} from "../types/index.js";
import { getTranslations, isDefined } from "../utils/utils.js";

interface GetTranslatedPartnerParams {
  partnerId: string;
  languageId: Language;
  includeLogos?: boolean;
}
export const getTranslatedPartner = (
  state: Pick<StoreState, "partners" | "logos">,
  { partnerId, languageId, includeLogos = false }: GetTranslatedPartnerParams,
): TranslatedPartner | undefined => {
  const partner = state.partners[partnerId];
  if (!partner) return;

  const partnerTranslation = getTranslations(languageId, partner.translations);
  if (!partnerTranslation) return;

  const translatedPartner: TranslatedPartner = {
    id: partner.id,
    logos: includeLogos
      ? getTranslatedPartnerLogos(state, { logoIds: partner.logos, languageId })
      : [],
    translation: partnerTranslation,
  };

  return translatedPartner;
};

export const getTranslatedPartners = (
  state: Pick<StoreState, "partners" | "logos">,
  { partnerIds, languageId }: { partnerIds?: string[]; languageId: Language },
): TranslatedPartner[] => {
  return (partnerIds ? partnerIds : Object.keys(state.partners))
    .map((partnerId) => {
      return getTranslatedPartner(state, {
        partnerId,
        languageId,
        includeLogos: true,
      });
    })
    .filter(isDefined);
};

const getTranslatedPartnerLogo = (
  state: Pick<StoreState, "partners" | "logos">,
  { logoId, languageId }: { logoId: string; languageId: Language },
): TranslatedPartnerLogo | undefined => {
  const logo = state.logos[logoId];
  if (!logo) return;

  const partner = state.partners[logo.partnerId];
  if (!partner) return;

  const translatedPartner = getTranslatedPartner(state, {
    partnerId: logo.partnerId,
    languageId,
    includeLogos: false,
  });

  const logoTranslation = getTranslations(languageId, logo.translations);

  if (!translatedPartner || !logoTranslation) return;

  return {
    id: logoId,
    background: logo.background,
    translation: logoTranslation,
    partner: translatedPartner,
  };
};

export const getTranslatedPartnerLogos = (
  state: Pick<StoreState, "partners" | "logos">,
  { logoIds, languageId }: { logoIds: string[]; languageId: Language },
): TranslatedPartnerLogo[] => {
  return logoIds
    .map((logoId) => {
      const partnerLogo = getTranslatedPartnerLogo(state, {
        logoId,
        languageId,
      });

      return partnerLogo;
    })
    .filter(isDefined);
};

export const getSelectedLogoIds = (
  state: Pick<StoreState, "modules">,
  { logoCategory, moduleId }: { moduleId: string; logoCategory: LogoCategory },
): string[] => {
  const currentModule = state.modules.byId[moduleId];
  const logoIds =
    (currentModule as Module<FooterModuleSettings>).settings.logoCategories[
      logoCategory
    ] || [];

  return logoIds;
};

export const getSouthTyrolLogo = (
  state: Pick<StoreState, "partners" | "logos">,
  languageId: Language,
) =>
  getTranslatedPartnerLogo(state, {
    languageId,
    logoId: "9eb9e147-0474-48e9-97f5-06ea20c59ade",
  });

export const selectTranslatedPartner = createAppSelector(
  [
    (state) => state.partners,
    (state) => state.logos,
    (_, params: GetTranslatedPartnerParams) => params,
  ],
  (partners, logos, params) =>
    getTranslatedPartner({ partners, logos }, params),
);

export const selectSouthTyrolLogo = createAppSelector(
  [
    (state) => state.partners,
    (state) => state.logos,
    (_, languageId: Language) => languageId,
    (_, _languageId: Language, showSouthTyrolLogo: boolean) =>
      showSouthTyrolLogo,
  ],
  (partners, logos, languageId, showSouthTyrolLogo) =>
    showSouthTyrolLogo
      ? getSouthTyrolLogo({ partners, logos }, languageId)
      : undefined,
);

export const selectPartnerLogoCategories = createAppSelector(
  [
    (state) => state.partners,
    (state) => state.logos,
    (_, logoCategories: LogoCategories) => logoCategories,
    (_, _logoCategories: LogoCategories, languageId: Language) => languageId,
  ],
  (partners, logos, logoCategories, languageId) =>
    logoCategories.map((logoIds) =>
      getTranslatedPartnerLogos({ partners, logos }, { logoIds, languageId }),
    ),
);
